import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Scale, Target, ClipboardX, AlarmClock, TrendingDown, Shuffle as ShuffleIcon,
  Star, Shuffle, Dumbbell, Trophy, Activity, BarChart3,
  Shield, Check, ArrowRight, Quote, Sparkles, ChevronDown,
} from "lucide-react";

const ICON_MAP = {
  Scale, Target, ClipboardX, AlarmClock, TrendingDown,
  Shuffle: ShuffleIcon, Star, Dumbbell, Trophy, Activity, BarChart3, Shield,
};
const Icon = ({ name, ...props }) => {
  const C = ICON_MAP[name] || Sparkles;
  return <C {...props} />;
};

export default function LandingPage() {
  const { user, membershipsLoaded } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [billing, setBilling] = useState("monthly"); // monthly | yearly
  const [legalDoc, setLegalDoc] = useState(null); // {label, content}

  // Eingeloggte User → Dashboard
  useEffect(() => {
    if (membershipsLoaded && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, membershipsLoaded, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/landing/content");
        setContent(data);
        if (data?.seo?.meta_title) document.title = data.seo.meta_title;
      } catch { /* ignore */ }
    })();
  }, []);

  if (!content) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="font-display text-zinc-500 tracking-wider uppercase text-sm">Lädt…</div>
      </div>
    );
  }

  const { hero, problems, solution, features, screenshots, social_proof, pricing, faq, cta_final, footer } = content;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 font-sans antialiased" data-testid="landing-page">
      {/* NAV */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-zinc-950/80 border-b border-zinc-900">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 lg:px-12 h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="lp-logo">
            <div className="h-8 w-8 bg-brand rounded-md flex items-center justify-center">
              <Shield className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">KickBalance</span>
          </Link>
          <div className="hidden md:flex items-center gap-1 text-sm">
            <a href="#funktionen" className="px-3 py-1.5 text-zinc-400 hover:text-brand-lighter transition">Funktionen</a>
            <a href="#preise" className="px-3 py-1.5 text-zinc-400 hover:text-brand-lighter transition">Preise</a>
            <a href="#faq" className="px-3 py-1.5 text-zinc-400 hover:text-brand-lighter transition">FAQ</a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-brand-lighter hover:bg-zinc-900 text-xs uppercase tracking-wider" data-testid="lp-login-btn">
                Anmelden
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="bg-brand hover:bg-brand-light text-white rounded-md text-xs uppercase tracking-wider" data-testid="lp-register-btn">
                Kostenlos starten
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-brand/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[40rem] h-[40rem] bg-brand/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-20 sm:pt-24 sm:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand/40 bg-brand/10 text-brand-lighter text-[11px] uppercase tracking-wider mb-6" data-testid="lp-hero-kicker">
              <Sparkles className="h-3.5 w-3.5" /> {hero.kicker}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight whitespace-pre-line" data-testid="lp-hero-headline">
              {hero.headline}
            </h1>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-2xl" data-testid="lp-hero-subtitle">
              {hero.subtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to={hero.cta_primary_link || "/register"}>
                <Button size="lg" className="bg-brand hover:bg-brand-light text-white rounded-md font-semibold tracking-wide w-full sm:w-auto h-12 px-7" data-testid="lp-hero-cta-primary">
                  {hero.cta_primary} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <a href={hero.cta_secondary_link || "#funktionen"} className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="bg-transparent border-zinc-700 hover:bg-zinc-900 hover:border-brand-lighter text-zinc-200 rounded-md w-full h-12 px-7" data-testid="lp-hero-cta-secondary">
                  {hero.cta_secondary}
                </Button>
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-zinc-500">
              <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-lighter" /> Kostenloser Tarif</div>
              <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-lighter" /> DSGVO-konform</div>
              <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-lighter" /> Made in Germany</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-brand/20 via-transparent to-brand-lighter/20 blur-2xl rounded-2xl" />
              <div className="relative rounded-xl border border-zinc-800 bg-zinc-900/60 p-2 shadow-2xl">
                {hero.image_url ? (
                  <img src={hero.image_url} alt="KickBalance Dashboard" className="w-full rounded-md" />
                ) : (
                  <MockDashboard />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="border-y border-zinc-900 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="max-w-3xl mb-12">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="lp-problems-title">
              {problems.title}
            </h2>
            <p className="mt-4 text-zinc-400 text-base sm:text-lg">{problems.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lp-problems-grid">
            {problems.cards.map((c, i) => (
              <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-6 hover:bg-zinc-900 hover:border-zinc-700 transition">
                <div className="h-10 w-10 rounded-md bg-rose-500/10 flex items-center justify-center mb-4">
                  <Icon name={c.icon} className="h-5 w-5 text-rose-400" />
                </div>
                <div className="font-semibold text-zinc-100">{c.title}</div>
                <div className="text-sm text-zinc-400 mt-2 leading-relaxed">{c.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-3">{solution.kicker}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" data-testid="lp-solution-title">
                {solution.title}
              </h2>
              <p className="mt-5 text-zinc-400 text-base sm:text-lg leading-relaxed">{solution.description}</p>
              <ul className="mt-8 space-y-3">
                {solution.highlights?.map((h, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <div className="h-5 w-5 rounded bg-brand/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-brand-lighter" />
                    </div>
                    <span className="text-zinc-300 text-sm">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { v: "12", l: "Skill-Attribute" },
                { v: "<1s", l: "KI-Balance" },
                { v: "100%", l: "Datentrennung" },
                { v: "0 €", l: "Einstieg" },
              ].map((s) => (
                <div key={s.l} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-6">
                  <div className="font-display text-3xl sm:text-4xl font-bold text-brand-lighter tabular-nums">{s.v}</div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="funktionen" className="border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-3">{features.kicker}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="lp-features-title">
              {features.title}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lp-features-grid">
            {features.cards.map((f, i) => (
              <div key={i} className="group rounded-lg border border-zinc-800 bg-gradient-to-br from-zinc-900/60 to-zinc-950 p-6 hover:from-zinc-900 hover:to-zinc-900/40 hover:border-brand-lighter/40 transition">
                <div className="h-11 w-11 rounded-md bg-brand/20 group-hover:bg-brand/30 flex items-center justify-center mb-5 transition">
                  <Icon name={f.icon} className="h-5 w-5 text-brand-lighter" />
                </div>
                <div className="font-semibold text-zinc-100 text-base">{f.title}</div>
                <div className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCREENSHOTS */}
      {screenshots?.items?.length > 0 && (
        <section className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
            <div className="max-w-3xl mb-12">
              <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-3">{screenshots.kicker}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{screenshots.title}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4" data-testid="lp-screenshots-grid">
              {screenshots.items.map((s, i) => (
                <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
                  <div className="aspect-[4/3] bg-zinc-900 flex items-center justify-center text-[10px] uppercase tracking-wider text-zinc-600">
                    {s.image_url
                      ? <img src={s.image_url} alt={s.caption} className="w-full h-full object-cover" />
                      : <span>Screenshot · {s.device}</span>}
                  </div>
                  <div className="p-4 text-xs text-zinc-400">{s.caption}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SOCIAL PROOF */}
      <section className="border-y border-zinc-900 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{social_proof.title}</h2>
          </div>
          {social_proof.stats?.length > 0 && (
            <div className="grid grid-cols-3 gap-3 max-w-3xl mx-auto mb-16" data-testid="lp-stats">
              {social_proof.stats.map((s, i) => (
                <div key={i} className="text-center rounded-lg border border-zinc-800 bg-zinc-900/40 p-6">
                  <div className="font-display text-2xl sm:text-3xl font-bold text-brand-lighter tabular-nums">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          )}
          {social_proof.testimonials?.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="lp-testimonials">
              {social_proof.testimonials.map((t, i) => (
                <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-6 sm:p-8">
                  <Quote className="h-6 w-6 text-brand-lighter mb-4" />
                  <p className="text-zinc-200 text-base leading-relaxed italic">&bdquo;{t.quote}&ldquo;</p>
                  <div className="mt-5 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center font-display font-bold text-brand-lighter text-sm">
                      {(t.author || "").charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-zinc-100">{t.author}</div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PRICING */}
      <section id="preise" className="relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-3">{pricing.kicker}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{pricing.title}</h2>
            <p className="mt-4 text-zinc-400">{pricing.subtitle}</p>
            <div className="inline-flex items-center gap-1 mt-6 p-1 rounded-md border border-zinc-800 bg-zinc-900/40" data-testid="lp-billing-toggle">
              <button onClick={() => setBilling("monthly")}
                className={"px-4 py-1.5 rounded text-xs uppercase tracking-wider transition " + (billing === "monthly" ? "bg-brand text-white" : "text-zinc-400 hover:text-zinc-100")}
                data-testid="lp-billing-monthly">Monatlich</button>
              <button onClick={() => setBilling("yearly")}
                className={"px-4 py-1.5 rounded text-xs uppercase tracking-wider transition " + (billing === "yearly" ? "bg-brand text-white" : "text-zinc-400 hover:text-zinc-100")}
                data-testid="lp-billing-yearly">Jährlich <span className="ml-1 text-brand-lighter">−17%</span></button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3 max-w-5xl mx-auto" data-testid="lp-pricing-grid">
            {pricing.plans.map((p, i) => (
              <div key={i}
                className={"relative rounded-lg p-6 sm:p-8 " + (p.featured
                  ? "border-2 border-brand bg-gradient-to-br from-brand/10 to-zinc-900/40 shadow-lg shadow-brand/10"
                  : "border border-zinc-800 bg-zinc-900/40")}>
                {p.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand text-white text-[10px] uppercase tracking-wider font-semibold">
                    Beliebt
                  </div>
                )}
                <div className="font-semibold text-zinc-100">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{p.tagline}</div>
                <div className="mt-5">
                  <span className="font-display text-3xl sm:text-4xl font-bold text-zinc-100 tabular-nums">
                    {billing === "monthly" ? p.price_monthly : p.price_yearly}
                  </span>
                  {!(billing === "yearly" && p.price_yearly === "auf Anfrage") && p.price_monthly !== "auf Anfrage" && (
                    <span className="text-xs text-zinc-500 ml-1">/ {billing === "monthly" ? "Monat" : "Jahr"}</span>
                  )}
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex gap-2 items-start text-sm text-zinc-300">
                      <Check className="h-4 w-4 text-brand-lighter shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <a href={p.cta_link || "#"} className="block mt-7">
                  <Button className={"w-full rounded-md " + (p.featured ? "bg-brand hover:bg-brand-light text-white" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100")}
                    data-testid={`lp-pricing-cta-${i}`}>
                    {p.cta}
                  </Button>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-y border-zinc-900 bg-zinc-950">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-3">{faq.kicker}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{faq.title}</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="lp-faq">
            {faq.items.map((q, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="border-zinc-800">
                <AccordionTrigger className="text-left font-semibold text-zinc-100 hover:text-brand-lighter hover:no-underline">
                  {q.question}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed">
                  {q.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-brand/20 via-transparent to-brand-lighter/10" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28 text-center">
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            {cta_final.title}
          </h2>
          <p className="mt-5 text-zinc-300 text-lg">{cta_final.subtitle}</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to={cta_final.cta_primary_link || "/register"}>
              <Button size="lg" className="bg-brand hover:bg-brand-light text-white rounded-md font-semibold w-full sm:w-auto h-12 px-8" data-testid="lp-final-cta-primary">
                {cta_final.cta_primary} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to={cta_final.cta_secondary_link || "/register"}>
              <Button size="lg" variant="outline" className="bg-transparent border-zinc-700 hover:bg-zinc-900 text-zinc-200 rounded-md w-full sm:w-auto h-12 px-8" data-testid="lp-final-cta-secondary">
                {cta_final.cta_secondary}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-900 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 grid sm:grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="h-7 w-7 bg-brand rounded-md flex items-center justify-center">
                <Shield className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-display text-base font-bold">KickBalance</span>
            </div>
            <div className="text-zinc-400 text-xs leading-relaxed">{footer.tagline}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Produkt</div>
            <ul className="space-y-2 text-zinc-400 text-xs">
              <li><a href="#funktionen" className="hover:text-brand-lighter">Funktionen</a></li>
              <li><a href="#preise" className="hover:text-brand-lighter">Preise</a></li>
              <li><a href="#faq" className="hover:text-brand-lighter">FAQ</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Kontakt</div>
            <ul className="space-y-2 text-zinc-400 text-xs">
              {footer.address && <li>{footer.address}</li>}
              {footer.email && <li><a href={`mailto:${footer.email}`} className="hover:text-brand-lighter">{footer.email}</a></li>}
              {footer.phone && <li>{footer.phone}</li>}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Rechtliches</div>
            <ul className="space-y-2 text-zinc-400 text-xs">
              {footer.links?.map((l, i) => {
                const hasContent = (l.content || "").trim().length > 0;
                if (hasContent) {
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => setLegalDoc({ label: l.label, content: l.content })}
                        className="hover:text-brand-lighter cursor-pointer"
                        data-testid={`lp-legal-${i}`}
                      >
                        {l.label}
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={i}><a href={l.url || "#"} className="hover:text-brand-lighter">{l.label}</a></li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-5 sm:px-8 lg:px-12 py-5 text-center text-[10px] uppercase tracking-wider text-zinc-600">
          © {new Date().getFullYear()} KickBalance · Made with ⚽ in Germany
        </div>
      </footer>

      <LegalDialog doc={legalDoc} onClose={() => setLegalDoc(null)} />
    </div>
  );
}

function MockDashboard() {
  return (
    <div className="bg-zinc-950 rounded-lg p-4 text-xs">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-1.5 rounded-full bg-rose-500" />
          <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </div>
        <div className="text-[9px] uppercase tracking-wider text-zinc-600">KickBalance · U15</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[
          { l: "Spieler", v: "34" },
          { l: "∅ Overall", v: "6.8" },
          { l: "∅ Anwesenheit", v: "89 %" },
        ].map((s) => (
          <div key={s.l} className="rounded border border-zinc-800 bg-zinc-900 p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-zinc-500">{s.l}</div>
            <div className="font-display font-bold text-zinc-100 text-base tabular-nums mt-0.5">{s.v}</div>
          </div>
        ))}
      </div>
      <div className="rounded border border-zinc-800 bg-zinc-900 p-2.5 mb-2">
        <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-2">KI-Team-Balance</div>
        <div className="grid grid-cols-2 gap-2">
          {["Team A", "Team B"].map((t, i) => (
            <div key={t} className="rounded bg-zinc-950 p-2 border border-zinc-800">
              <div className="text-[9px] font-semibold text-brand-lighter mb-1">{t}</div>
              <div className="space-y-1">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="h-3 w-3 rounded bg-zinc-800" />
                      <span className="text-[9px] text-zinc-400">Spieler {i * 3 + n}</span>
                    </div>
                    <span className="text-[9px] tabular-nums text-zinc-500">{(7.2 - n * 0.3).toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded border border-zinc-800 bg-zinc-900 p-2.5">
        <div className="flex items-center justify-between">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Top-Spieler</div>
          <div className="text-[9px] text-brand-lighter">Diese Woche</div>
        </div>
        <div className="mt-2 space-y-1.5">
          {[
            { n: "Carl Lewis", v: 8.1 },
            { n: "Fabian Müller", v: 7.8 },
            { n: "Yusuf Demir", v: 7.5 },
          ].map((p) => (
            <div key={p.n} className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-200">{p.n}</span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-1 rounded bg-zinc-800 overflow-hidden">
                  <div className="h-full bg-brand-lighter" style={{ width: `${(p.v / 10) * 100}%` }} />
                </div>
                <span className="text-[10px] tabular-nums font-semibold text-brand-lighter">{p.v}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LegalDialog({ doc, onClose }) {
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lp-legal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{doc?.label}</DialogTitle>
        </DialogHeader>
        <div className="prose prose-invert prose-sm max-w-none text-zinc-300 leading-relaxed">
          {doc && <MarkdownLite text={doc.content} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Minimaler Markdown-Renderer für rechtliche Inhalte.
 * Unterstützt: # H1, ## H2, ### H3, **bold**, *italic*, [link](url),
 * Liste (- oder *), Absätze (Leerzeile als Trenner). Plain HTML wird escaped.
 */
function MarkdownLite({ text }) {
  if (!text) return null;
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => {
    let r = esc(s);
    r = r.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-brand-lighter underline">$1</a>');
    r = r.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-zinc-100">$1</strong>');
    r = r.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    return r;
  };
  const blocks = text.replace(/\r\n/g, "\n").split(/\n\n+/);
  return (
    <>
      {blocks.map((block, idx) => {
        const lines = block.split("\n");
        // Liste?
        if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
          return (
            <ul key={idx} className="list-disc pl-5 my-3 space-y-1">
              {lines.map((l, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: inline(l.replace(/^\s*[-*]\s+/, "")) }} />
              ))}
            </ul>
          );
        }
        const first = lines[0];
        if (/^###\s+/.test(first)) {
          return <h3 key={idx} className="font-display text-base font-semibold text-zinc-100 mt-5 mb-1" dangerouslySetInnerHTML={{ __html: inline(first.replace(/^###\s+/, "")) }} />;
        }
        if (/^##\s+/.test(first)) {
          return <h2 key={idx} className="font-display text-lg font-semibold text-zinc-100 mt-6 mb-2" dangerouslySetInnerHTML={{ __html: inline(first.replace(/^##\s+/, "")) }} />;
        }
        if (/^#\s+/.test(first)) {
          return <h1 key={idx} className="font-display text-xl font-bold text-zinc-100 mt-6 mb-2" dangerouslySetInnerHTML={{ __html: inline(first.replace(/^#\s+/, "")) }} />;
        }
        return <p key={idx} className="my-3" dangerouslySetInnerHTML={{ __html: lines.map(inline).join("<br />") }} />;
      })}
    </>
  );
}

